// Shared styling primitives and small components for the SLT oversight pages.
import React from 'react';
import { useNavigate } from 'react-router-dom';

export const BRAND = {
  teal: '#75B9BE',
  cream: '#FDF6EC',
  charcoal: '#2D4042',
  coral: '#FF7F50',
  white: '#FFFFFF',
  muted: '#5D7375'
};

export const cardStyle = {
  background: BRAND.white,
  border: `2px solid ${BRAND.charcoal}`,
  borderRadius: '16px',
  boxShadow: '4px 4px 0 rgba(45, 64, 66, 0.1)',
  padding: '18px 20px'
};

export const thStyle = {
  background: BRAND.white,
  borderBottom: `2px solid ${BRAND.charcoal}`,
  color: BRAND.muted,
  fontFamily: 'Nunito, sans-serif',
  fontSize: '0.75rem',
  fontWeight: 800,
  letterSpacing: '0.05em',
  padding: '10px 12px',
  textAlign: 'left',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
};

export const tdStyle = {
  borderBottom: '1px solid rgba(45, 64, 66, 0.12)',
  color: BRAND.charcoal,
  fontSize: '0.92rem',
  padding: '12px',
  verticalAlign: 'middle'
};

export const searchInputStyle = {
  background: BRAND.white,
  border: `2px solid ${BRAND.charcoal}`,
  borderRadius: '10px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  fontSize: '0.95rem',
  maxWidth: '380px',
  padding: '10px 14px',
  width: '100%'
};

export const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
export const formatDateTime = (value) => (value ? new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

export const TagBadge = ({ children, tone = 'teal' }) => (
  <span style={{
    background: tone === 'coral' ? 'rgba(255, 127, 80, 0.14)' : 'rgba(117, 185, 190, 0.18)',
    border: `1px solid ${tone === 'coral' ? BRAND.coral : BRAND.teal}`,
    borderRadius: '999px',
    color: BRAND.charcoal,
    display: 'inline-block',
    fontSize: '0.72rem',
    fontWeight: 800,
    margin: '1px 4px 1px 0',
    padding: '2px 9px',
    whiteSpace: 'nowrap'
  }}>
    {children}
  </span>
);

export const StatPill = ({ value, suffix = '%', good = 70 }) => {
  if (value === null || value === undefined) return <span style={{ color: BRAND.muted }}>—</span>;
  const color = value >= good ? '#1F7A3A' : (value >= good - 25 ? '#B45309' : '#B3261E');
  return <span style={{ color, fontWeight: 800 }}>{value}{suffix}</span>;
};

// Column header with click-to-sort affordance and direction indicator.
export const SortableTh = ({ label, sortKey, sort, onSort, align = 'left' }) => {
  const active = sort?.key === sortKey;
  return (
    <th
      style={{ ...thStyle, cursor: 'pointer', textAlign: align, userSelect: 'none' }}
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      <span style={{ color: active ? BRAND.coral : 'rgba(45,64,66,0.35)', marginLeft: 5 }}>
        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );
};

export const sortRows = (rows, sort) => {
  if (!sort?.key) return rows;
  const sorted = [...rows].sort((a, b) => {
    const av = a[sort.key];
    const bv = b[sort.key];
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    if (typeof av === 'string') return av.localeCompare(bv, undefined, { sensitivity: 'base' });
    return av - bv;
  });
  return sort.dir === 'desc' ? sorted.reverse() : sorted;
};

export const Paginator = ({ page, totalPages, onPage, summary }) => (
  <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', padding: '12px 20px' }}>
    <span style={{ color: BRAND.muted, fontSize: '0.85rem' }}>{summary}</span>
    <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        style={{ background: BRAND.white, border: `2px solid ${page <= 1 ? 'rgba(45,64,66,0.25)' : BRAND.charcoal}`, borderRadius: '8px', color: page <= 1 ? 'rgba(45,64,66,0.35)' : BRAND.charcoal, cursor: page <= 1 ? 'default' : 'pointer', fontFamily: 'Nunito, sans-serif', fontWeight: 800, padding: '6px 14px' }}
      >
        ← Prev
      </button>
      <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>Page {page} of {Math.max(totalPages, 1)}</span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
        style={{ background: BRAND.white, border: `2px solid ${page >= totalPages ? 'rgba(45,64,66,0.25)' : BRAND.charcoal}`, borderRadius: '8px', color: page >= totalPages ? 'rgba(45,64,66,0.35)' : BRAND.charcoal, cursor: page >= totalPages ? 'default' : 'pointer', fontFamily: 'Nunito, sans-serif', fontWeight: 800, padding: '6px 14px' }}
      >
        Next →
      </button>
    </div>
  </div>
);

// Keyboard-accessible clickable table row.
export const ClickableRow = ({ onActivate, children }) => (
  <tr
    className="slt-row clickable"
    tabIndex={0}
    role="link"
    onClick={onActivate}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }}
  >
    {children}
  </tr>
);

export const EmptyState = ({ title, hint }) => (
  <div style={{ color: BRAND.muted, padding: '40px 20px', textAlign: 'center' }}>
    <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.05rem', fontWeight: 800 }}>{title}</div>
    {hint && <div style={{ fontSize: '0.9rem', marginTop: 6 }}>{hint}</div>}
  </div>
);

// Recent digital tutorial attempts across the school; rows click through to
// the student's SLT profile. Used on the overview and students pages.
export const StudentActivityFeed = ({ items = [] }) => {
  const navigate = useNavigate();

  if (!items.length) {
    return <EmptyState title="No tutorials taken yet" hint="Activity will appear here as soon as students take tutorials." />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {items.map((item) => (
        <div
          key={item.attemptId}
          className={`slt-row${item.studentId ? ' clickable' : ''}`}
          role={item.studentId ? 'link' : undefined}
          tabIndex={item.studentId ? 0 : undefined}
          onClick={() => item.studentId && navigate(`/slt/students/${item.studentId}`)}
          onKeyDown={(e) => { if (e.key === 'Enter' && item.studentId) navigate(`/slt/students/${item.studentId}`); }}
          style={{ alignItems: 'baseline', borderBottom: '1px solid rgba(45,64,66,0.1)', display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '12px 6px' }}
        >
          <strong style={{ fontFamily: 'Nunito, sans-serif' }}>{item.studentName}</strong>
          <span style={{ color: BRAND.muted }}>{item.status === 'completed' ? 'completed' : 'started'}</span>
          <span style={{ fontWeight: 700 }}>{item.label}{item.lessonPlanTitle ? ` — ${item.lessonPlanTitle}` : ''}</span>
          {item.subject && <TagBadge>{item.subject}</TagBadge>}
          {item.totalAnswered > 0 && (
            <span>{item.correct}/{item.totalAnswered} correct (<StatPill value={item.accuracy} />)</span>
          )}
          <span style={{ color: BRAND.muted, fontSize: '0.85rem' }}>with {item.teacherName}</span>
          <span style={{ color: BRAND.muted, fontSize: '0.85rem', marginLeft: 'auto' }}>{formatDateTime(item.when)}</span>
        </div>
      ))}
    </div>
  );
};
